import React, { useEffect, useRef, useState } from "react";
import "./Faculty.css";
import ActionBar from "../Components/BottomActionBar/ActionBar";
import UpdateComponent from "../Components/UpdateComponent/UpdateComponent";
import { facultyAPI } from "./api";
import DataTable from "./DataTable";

function Faculty() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [mobile, setMobile] = useState("");
  const [remark, setRemark] = useState("");
  const [activeStatus, setActiveStatus] = useState("");

  const [tableData, setTableData] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [showEditCols, setShowEditCols] = useState(false);
  const [editRows, setEditRows] = useState({});
  const [selectedForUpdate, setSelectedForUpdate] = useState({});
  const [selectedForDelete, setSelectedForDelete] = useState({});
  const [insertMsg, setInsertMsg] = useState(null);
  const [updateMsg, setUpdateMsg] = useState(null);
  const [deleteMsg, setDeleteMsg] = useState(null);
  const [sortBy, setSortBy] = useState("");
  const [lastLoadParams, setLastLoadParams] = useState({});
  const lastLoadParamsRef = useRef({});

  const firstInputRef = useRef(null);
  useEffect(() => {
    firstInputRef.current?.focus();
  }, []);

  const handleActiveStatusToggle = (value) => {
    setActiveStatus((prev) => (prev === value ? "" : value));
  };

  const clearMessages = () => {
    setInsertMsg(null);
    setUpdateMsg(null);
    setDeleteMsg(null);
  };

  const buildParams = () => {
    const params = {};
    if (name.trim()) params.name = name.trim();
    if (email.trim()) params.email = email.trim();
    if (activeStatus) params.active_status = activeStatus === "Active" ? "1" : "0";
    return params;
  };

  const handleInsert = async () => {
    setUpdateMsg(null);
    setDeleteMsg(null);

    if (!name.trim() || !email.trim() || !address.trim() || !activeStatus) {
      alert("Name, Email, Resi. Address and Active Status are required fields.");
      return;
    }
    const emailPat = /^[^@\s]+@[^@.\s]+\.[^@\s]+$/;
    if (!emailPat.test(email)) {
      alert("Please enter a valid email address.");
      return;
    }
    if (mobile.trim() && mobile.trim().length !== 10) {
      alert("Mobile number must be exactly 10 digits.");
      return;
    }

    try {
      const res = await facultyAPI.create({
        faculty_name: name.trim(),
        email: email.trim(),
        address: address.trim(),
        active_status: activeStatus === "Active" ? "1" : "0",
        ...(mobile.trim() && { telephone: mobile.trim() }),
        ...(remark.trim() && { remark: remark.trim() }),
      });
      if (res.ok) setInsertMsg({ text: "Record inserted successfully.", success: true });
      else setInsertMsg({ text: "Insert failed.", success: false });
    } catch {
      setInsertMsg({ text: "Insert failed.", success: false });
    }
  };

  const sortTableData = (data, sortOption) => {
    if (!sortOption) return data;
    const sorted = [...data];
    switch (sortOption) {
      case "Name":
        return sorted.sort((a, b) => String(a.faculty_name || "").localeCompare(String(b.faculty_name || "")));
      case "Email":
        return sorted.sort((a, b) => String(a.email || "").localeCompare(String(b.email || "")));
      case "Active Status":
        return sorted.sort((a, b) => String(a.active_status || "").localeCompare(String(b.active_status || "")));
      default:
        return sorted;
    }
  };

  const refreshTable = async () => {
    try {
      const data = await facultyAPI.getAll(lastLoadParamsRef.current);
      setTableData(sortTableData(data || [], sortBy));
    } catch {
      setTableData([]);
    }
  };

  const handleUpdate = async () => {
    setInsertMsg(null);
    setUpdateMsg(null);
    setDeleteMsg(null);
    setShowEditCols(true);
    setEditRows({});
    const params = buildParams();
    setLastLoadParams(params);
    lastLoadParamsRef.current = params;
    try {
      const data = await facultyAPI.getAll(params);
      setTableData(data || []);
      setShowTable(true);
    } catch {
      setTableData([]);
      setShowTable(true);
    }
  };

  const handleView = async () => {
    setInsertMsg(null);
    setUpdateMsg(null);
    setDeleteMsg(null);
    setShowEditCols(false);
    setEditRows({});
    const params = buildParams();
    setLastLoadParams(params);
    lastLoadParamsRef.current = params;
    try {
      const data = await facultyAPI.getAll(params);
      setTableData(data || []);
      setShowTable(true);
    } catch {
      setTableData([]);
      setShowTable(true);
    }
  };

  const toggleEditRow = (idx, row) => {
    setEditRows((prev) => {
      if (prev[idx]) {
        const next = { ...prev };
        delete next[idx];
        return next;
      }
      return {
        ...prev,
        [idx]: {
          faculty_name: row.faculty_name ?? "",
          faculty_type: row.faculty_type ?? "",
          email: row.email ?? "",
          address: row.address ?? "",
          telephone: row.telephone ?? "",
          remark: row.remark ?? "",
          active_status: (row.active_status === '1' || row.active_status === 'Active') ? 'Active' : 'Inactive',
        },
      };
    });

    setSelectedForUpdate((prev) => {
      if (prev[idx]) {
        const next = { ...prev };
        delete next[idx];
        return next;
      }
      return { ...prev, [idx]: true };
    });
  };

  const toggleDeleteRow = (idx) => {
    setSelectedForDelete((prev) => {
      if (prev[idx]) {
        const next = { ...prev };
        delete next[idx];
        return next;
      }
      return { ...prev, [idx]: true };
    });
  };

  const handleUpdateSelected = async () => {
    console.log("[UPDATE] handleUpdateSelected triggered");
    console.log("[UPDATE] selectedForUpdate:", selectedForUpdate);
    console.log("[UPDATE] editRows:", editRows);
    console.log("[UPDATE] tableData:", tableData);

    const selectedIndexes = Object.keys(selectedForUpdate).filter((idx) => selectedForUpdate[idx]);
    console.log("[UPDATE] selectedIndexes:", selectedIndexes);

    if (selectedIndexes.length === 0) {
      console.log("[UPDATE] No records selected.");
      setUpdateMsg({ text: "No records selected for update.", success: false });
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const idx of selectedIndexes) {
      const row = tableData[idx];
      const editedData = editRows[idx];
      console.log(`[UPDATE] Processing idx=${idx}, row:`, row, "editedData:", editedData);

      if (!editedData) {
        console.log(`[UPDATE] idx=${idx}: editedData is null/undefined, skipping.`);
        continue;
      }

      const tel = String(editedData.telephone ?? "").trim();
      console.log(`[UPDATE] idx=${idx}: tel="${tel}", length=${tel.length}`);
      if (tel && tel.length !== 10) {
        console.log(`[UPDATE] idx=${idx}: telephone validation failed.`);
        setUpdateMsg({ text: `Row ${+idx + 1}: Mobile number must be exactly 10 digits.`, success: false });
        failCount++;
        continue;
      }

      const payload = {
        faculty_name: editedData.faculty_name,
        email: editedData.email,
        address: editedData.address,
        telephone: tel,
        remark: editedData.remark,
        active_status: editedData.active_status === "Active" ? "1" : "0",
      };
      console.log(`[UPDATE] idx=${idx}: API URL name="${row.faculty_name}" email="${row.email}"`);
      console.log(`[UPDATE] idx=${idx}: payload:`, payload);

      try {
        const res = await facultyAPI.update(row.faculty_name, row.email, payload);
        console.log(`[UPDATE] idx=${idx}: response status=${res.status}, ok=${res.ok}`);

        if (res.ok) {
          successCount++;
        } else {
          const errBody = await res.json().catch(() => ({}));
          console.log(`[UPDATE] idx=${idx}: error response body:`, errBody);
          failCount++;
        }
      } catch (err) {
        console.log(`[UPDATE] idx=${idx}: exception thrown:`, err);
        failCount++;
      }
    }

    if (successCount > 0) {
      setUpdateMsg({ text: `${successCount} record(s) updated successfully.`, success: true });
      await refreshTable();
    }
    if (failCount > 0) {
      setUpdateMsg({ text: `${failCount} record(s) failed to update.`, success: false });
    }

    setSelectedForUpdate({});
    setEditRows({});
  };

  const handleDeleteSelected = async () => {
    const selectedIndexes = Object.keys(selectedForDelete).filter((idx) => selectedForDelete[idx]);

    if (selectedIndexes.length === 0) {
      setDeleteMsg({ text: "No records selected for delete.", success: false });
      return;
    }

    let successCount = 0;
    const failMsgs = [];

    for (const idx of selectedIndexes) {
      const row = tableData[idx];

      if (row.active_status === '1' || row.active_status === 'Active') {
        alert(`"${row.faculty_name}" is Active and cannot be deleted. Please mark them Inactive first.`);
        continue;
      }

      try {
        console.log(`[DELETE] idx=${idx}: calling checkInUse with faculty_code=${row.faculty_code} (type: ${typeof row.faculty_code})`);
        const inUseRes = await facultyAPI.checkInUse(row.faculty_code);
        console.log(`[DELETE] idx=${idx}: checkInUse response:`, inUseRes);
        if (inUseRes.in_use) {
          alert(`"${row.faculty_name}" is currently being used in Batch No. ${inUseRes.batch_sr_no} and cannot be deleted.`);
          continue;
        }
        const res = await facultyAPI.delete(row.faculty_name, row.email);
        if (res.ok) {
          successCount++;
        } else {
          const errBody = await res.json().catch(() => ({}));
          failMsgs.push(errBody.message || `Failed to delete "${row.faculty_name}".`);
        }
      } catch {
        failMsgs.push(`Failed to delete "${row.faculty_name}".`);
      }
    }

    if (successCount > 0) {
      setDeleteMsg({ text: `${successCount} record(s) deleted successfully.`, success: true });
      await refreshTable();
    }
    if (failMsgs.length > 0) {
      setDeleteMsg({ text: failMsgs.join(' '), success: false });
    }

    setSelectedForDelete({});
  };

  const handleReset = () => {
    setName("");
    setEmail("");
    setAddress("");
    setMobile("");
    setRemark("");
    setActiveStatus("");
    setShowEditCols(false);
    setEditRows({});
    setSelectedForUpdate({});
    setSelectedForDelete({});
    setInsertMsg(null);
    setUpdateMsg(null);
    setDeleteMsg(null);
    setTableData([]);
    setShowTable(false);
    setSortBy("");
    firstInputRef.current?.focus();
  };

  const handleSortChange = (sortOption) => {
    setSortBy(sortOption);
    if (tableData.length > 0) {
      setTableData(sortTableData(tableData, sortOption));
    }
  };

  const columns = [
    { header: "Name", key: "faculty_name", accessor: (row) => row.faculty_name ?? "", editable: true },
    { header: "Email ID", key: "email", accessor: (row) => row.email ?? "", editable: true, inputProps: { type: "email" } },
    { header: "Resi. Address", key: "address", accessor: (row) => row.address ?? "", editable: true },
    { header: "Mobile No.", key: "telephone", accessor: (row) => row.telephone ?? "", editable: true, inputProps: { type: "text", maxLength: 10 } },
    { header: "Remarks", key: "remark", accessor: (row) => row.remark ?? "", editable: true },
    {
      header: "Active Status",
      key: "active_status",
      accessor: (row) => (row.active_status === '1' || row.active_status === 'Active') ? 'Active' : 'Inactive',
      editable: true,
      renderEdit: (idx, editRows, onChangeEditCell) => (
        <input
          type="checkbox"
          checked={editRows[idx]?.active_status === "Active"}
          onChange={(e) => onChangeEditCell(idx, "active_status", e.target.checked ? "Active" : "Inactive")}
        />
      ),
    },
  ];

  return (
    <div className="Body" onClick={clearMessages}>
      <form onSubmit={(e) => e.preventDefault()}>
        <table className="formTable">
          <tbody>
            <tr>
              <td className="tdLabel">Name <span className="important">*</span></td>
              <td className="tdField">
                <input className="courseNameInput" type="text" ref={firstInputRef} value={name} onChange={(e) => setName(e.target.value)} onFocus={clearMessages} />
              </td>
            </tr>
            <tr>
              <td className="tdLabel">Email <span className="important">*</span></td>
              <td className="tdField">
                <input className="courseNameInput" type="text" value={email} onChange={(e) => setEmail(e.target.value)} onFocus={clearMessages} />
              </td>
            </tr>
            <tr>
              <td className="tdLabel">Resi. Address <span className="important">*</span></td>
              <td className="tdField">
                <input className="courseNameInput" type="text" value={address} onChange={(e) => setAddress(e.target.value)} onFocus={clearMessages} />
              </td>
            </tr>
            <tr>
              <td className="tdLabel">Mobile No.</td>
              <td className="tdField">
                <input className="courseNameInput" type="text" value={mobile} onChange={(e) => setMobile(e.target.value)} onFocus={clearMessages} />
              </td>
            </tr>
            <tr>
              <td className="tdLabel" style={{ verticalAlign: "top" }}>Remark</td>
              <td className="tdField">
                <input className="courseNameInput" type="text" value={remark} onChange={(e) => setRemark(e.target.value)} onFocus={clearMessages} />
              </td>
            </tr>
            <tr>
              <td className="tdLabel">Active Status <span className="important">*</span></td>
              <td className="tdField">
                <label>
                  <input type="radio" name="activeStatus" value="Active" checked={activeStatus === "Active"} onChange={() => setActiveStatus("Active")} />{" "}Active
                </label>
                &nbsp;&nbsp;&nbsp;
                <label>
                  <input type="radio" name="activeStatus" value="Inactive" checked={activeStatus === "Inactive"} onChange={() => setActiveStatus("Inactive")} />{" "}Inactive
                </label>
              </td>
            </tr>
            <tr>
              <td colSpan={4}>
                <p className="remarks">
                  <span className="important">*</span> indicates a required field for 'INSERT' &amp; 'UPDATE'.
                </p>
              </td>
            </tr>
          </tbody>
        </table>
      </form>

      <ActionBar
        sortOptions={["Name", "Email", "Active Status"]}
        onView={handleView}
        onReset={handleReset}
        onInsert={handleInsert}
        onUpdate={handleUpdate}
        onSortChange={handleSortChange}
        selectedSort={sortBy}
      />

      {insertMsg && (
        <p style={{ textAlign: "center", fontSize: "11px", fontFamily: '"GEG-Body"', color: insertMsg.success ? "green" : "red", margin: "4px 0" }}>
          {insertMsg.text}
        </p>
      )}
      {updateMsg && (
        <p style={{ textAlign: "center", fontSize: "11px", fontFamily: '"GEG-Body"', color: updateMsg.success ? "green" : "red", margin: "4px 0" }}>
          {updateMsg.text}
        </p>
      )}
      {deleteMsg && (
        <p style={{ textAlign: "center", fontSize: "11px", fontFamily: '"GEG-Body"', color: deleteMsg.success ? "green" : "red", margin: "4px 0" }}>
          {deleteMsg.text}
        </p>
      )}

      {showTable && (
        <DataTable
          columns={columns}
          data={tableData}
          showEditCols={showEditCols}
          editRows={editRows}
          onToggleEditRow={toggleEditRow}
          onChangeEditCell={(idx, key, value) =>
            setEditRows((prev) => ({ ...prev, [idx]: { ...prev[idx], [key]: value } }))
          }
          selectedForDelete={selectedForDelete}
          onToggleDeleteRow={toggleDeleteRow}
          emptyMessage="No data found"
          rowKey={(row, idx) => row.email || `${row.faculty_name}-${idx}`}
        />
      )}

      {showTable && showEditCols && (
        <UpdateComponent onUpdateSelected={handleUpdateSelected} onDeleteSelected={handleDeleteSelected} />
      )}
    </div>
  );
}

export default Faculty;
